import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import logSignUpFailed from '@alltrails/amplitude/events/logSignUpFailed';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import useRequestCountryCode from '@alltrails/context/hooks/useRequestCountryCode';
import Typography from '@alltrails/core/components/Typography';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { ApiError } from '@alltrails/core/utils/api';
import { post } from '@alltrails/core/utils/post';
import { executeRecaptcha } from '@alltrails/core/utils/recaptcha-helpers';
import Button from '@alltrails/denali/components/Button';
import Checkbox from '@alltrails/denali/components/Checkbox';
import TextInput from '@alltrails/denali/components/TextInput';
import useFeatures from '@alltrails/features/hooks/useFeatures';
import type LoginResponse from '../../types/LoginResponse';
import { authSuccessCallback } from '../../utils/AuthHandlers';
import getClientId from '../../utils/getClientId';
import validateUserForm from '../../utils/validateUserForm';
import styles from './styles.module.scss';

const LABELS = defineMessages({
  SIGN_UP: {
    defaultMessage: 'Sign up'
  },
  FIRST_NAME: {
    defaultMessage: 'First name'
  },
  LAST_NAME: {
    defaultMessage: 'Last name'
  },
  EMAIL_ADDRESS: {
    defaultMessage: 'Email address'
  },
  PASSWORD: {
    defaultMessage: 'Password'
  }
});

type Props = {
  autoFocus?: boolean;
  referralLink?: string;
  handleRegistrationSuccess: (response: LoginResponse) => void;
  userEmailSubscribed?: boolean;
  grantType?: string | null;
  prefillEmail?: string;
  onEmailAlreadyExists?: (email: string) => void;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=6514%3A101082
export default function RegisterForm({
  autoFocus = false,
  referralLink,
  handleRegistrationSuccess,
  userEmailSubscribed,
  grantType,
  prefillEmail,
  onEmailAlreadyExists
}: Props) {
  const features = useFeatures();

  const {
    formattedDefaultMessages: { SIGN_UP, FIRST_NAME, LAST_NAME, EMAIL_ADDRESS, PASSWORD }
  } = useFormatMessage(LABELS);

  const intl = useIntl();
  const requestCountryCode = useRequestCountryCode();
  const [serverError, setServerError] = useState<ReactNode>();

  const [state, setState] = useState<{
    userEmailSubscribed: boolean;
    userRegLastName: string;
    userRegEmail: string;
    userRegPassword: string;
    userRegFirstName: string;
    submitting: boolean;
    showErrorsOnType: boolean;
  }>({
    userRegEmail: prefillEmail || '',
    userRegPassword: '',
    userRegFirstName: '',
    userRegLastName: '',
    userEmailSubscribed: Boolean(userEmailSubscribed),
    submitting: false,
    showErrorsOnType: false
  });

  const [errors, setErrors] = useState<{ userRegEmail?: string; userRegPassword?: string; userRegFirstName?: string; userRegLastName?: string }>({});

  function showEmailSubscribed() {
    return requestCountryCode !== 'US';
  }

  function handleSubscribedChange() {
    if (!showEmailSubscribed()) {
      return;
    }

    const { userEmailSubscribed } = state;
    setState({ ...state, userEmailSubscribed: !userEmailSubscribed });
  }

  function handleChange(name: string, value: string) {
    // we don't want to show errors on type unless a user has already tried to submit a form
    if (state.showErrorsOnType) {
      setErrors(validateUserForm(name, value, errors, intl).errors);
    }

    setState({ ...state, [name]: value });
  }

  async function submitRegister(e: React.KeyboardEvent<HTMLDivElement> | React.FormEvent) {
    e.preventDefault();
    logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });

    if (state.submitting) {
      return;
    }

    let validationErrors = {};
    validationErrors = validateUserForm('userRegFirstName', state.userRegFirstName, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegLastName', state.userRegLastName, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegEmail', state.userRegEmail, validationErrors, intl).errors;
    validationErrors = validateUserForm('userRegPassword', state.userRegPassword, validationErrors, intl).errors;

    if (Object.keys(validationErrors).length !== 0) {
      setErrors(validationErrors);
      setState({ ...state, showErrorsOnType: true });
      return;
    }

    setState({ ...state, submitting: true });

    try {
      let recaptcha;

      // on alpha we disable recaptcha for Cypress testing
      if (features.includes('recaptcha_user_signup_login')) {
        recaptcha = await executeRecaptcha('userSignupLogin');
      }

      const response = await post<LoginResponse>('/identity/auth/register', {
        client_id: getClientId(),
        ...(grantType && { grant_type: grantType }),
        user: {
          first_name: state.userRegFirstName,
          last_name: state.userRegLastName,
          email: state.userRegEmail,
          password: state.userRegPassword,
          create_session: true,
          subscribed: state.userEmailSubscribed,
          recaptcha,
          referral_code: referralLink
        }
      });

      authSuccessCallback(response, 'email', response.is_collab_list_invite);

      setErrors({});

      setState({
        userRegEmail: '',
        userRegPassword: '',
        userRegFirstName: '',
        userRegLastName: '',
        userEmailSubscribed: Boolean(userEmailSubscribed),
        submitting: false,
        showErrorsOnType: false
      });

      setServerError(null);

      handleRegistrationSuccess(response);
    } catch (e: any) {
      if (e instanceof ApiError && e.data?.errors?.length > 0) {
        const errorCode = e.data.errors[0].code;
        if (errorCode === 'registration_already_registered' && onEmailAlreadyExists) {
          onEmailAlreadyExists(state.userRegEmail);
          return;
        }

        if (e.data.errors[0].target === 'email') {
          setErrors({ ...errors, userRegEmail: e.data.errors[0].message });
        }
        setServerError(e.data.errors[0].message);
      } else {
        setServerError(<FormattedMessage defaultMessage="Error, please try again" />);
      }

      logSignUpFailed({
        authentication_type: 'email',
        failure_type: e instanceof ApiError && e.data?.errors?.length > 0 ? e.data.errors[0].code : e.status
      });
    } finally {
      setState({ ...state, submitting: false });
    }
  }

  return (
    <form className={styles.form} onSubmit={submitRegister}>
      <TextInput
        autoFocus={autoFocus && !prefillEmail}
        name="userRegFirstName"
        errorText={errors.userRegFirstName}
        value={state.userRegFirstName}
        labelText={FIRST_NAME}
        onChange={(value: string) => {
          handleChange('userRegFirstName', value);
        }}
      />
      <TextInput
        name="userRegLastName"
        errorText={errors.userRegLastName}
        value={state.userRegLastName}
        labelText={LAST_NAME}
        onChange={(value: string) => {
          handleChange('userRegLastName', value);
        }}
      />
      <TextInput
        name="userRegEmail"
        errorText={errors.userRegEmail}
        value={state.userRegEmail}
        labelText={EMAIL_ADDRESS}
        onChange={(value: string) => {
          if (serverError) {
            setServerError(null);
          }
          if (errors.userRegEmail) {
            setErrors({ userRegEmail: '' });
          }
          handleChange('userRegEmail', value);
        }}
        type="email"
      />
      <TextInput
        name="userRegPassword"
        errorText={errors.userRegPassword}
        value={state.userRegPassword}
        labelText={PASSWORD}
        onChange={(value: string) => {
          handleChange('userRegPassword', value);
        }}
        type="password"
      />
      {serverError && <div className={`${styles.serverError} ${styles.center}`}>{serverError}</div>}
      <div className={classNames(styles.bottom, styles.bottomMarginOnly)}>
        <Button text={SIGN_UP} disabled={state.submitting} type="submit" variant="primary" width="full" />
        {showEmailSubscribed() && (
          <Checkbox
            className={styles.subscribedContainer}
            id="emailSubscribed"
            size="sm"
            onChange={handleSubscribedChange}
            selected={state.userEmailSubscribed}
            labelElement={
              <Typography variant="text200">
                <FormattedMessage defaultMessage="Get helpful product tips and special offers" />
              </Typography>
            }
          />
        )}
      </div>
    </form>
  );
}
